import { smoothstep } from '../../core/math';
import { Rng } from '../../core/rng';
import { type Coast, type LandProfile, type Pier, floatY, landHeight, layPier, pierTip, shoreDistance, withPier } from '../../zones/IslandKit';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

const WATER = -0.6;
const DECK = WATER + 0.45;
const COAST: Coast = { x: 0, z: -4, r: 46, waves: [[3, 0.13, 0.7], [5, 0.087, -1.1]] };
const PROFILE: LandProfile = { seaFloor: -5.5, shore: 0.2, rise: 1.8, inland: 18, beach: 3, hills: 1.2, seed: 3001 };
const PIER: Pier = { from: { x: 0, z: 38 }, dir: 0, length: 20, halfWidth: 1.5 };

/** Haven Isle crafting stations (interactable kind `station`). */
export const HAVEN_STATIONS = ['workshop', 'research', 'dispatch', 'garden'] as const;

/**
 * HAVEN ISLE — the player's stronghold: a small green island ringed by deep
 * water. A pier (south) leads up a cobbled road to the manor plaza where
 * Steward Pim waits; the workshop (west), research tower (east), herb garden
 * (south-west) and ship dispatch board (by the pier) are `station`
 * interactables. The dock portal at the pier tip sails to the Gleaming Sea
 * (`fromHaven`). Spawns: `start` (road below the plaza), `fromSea` (pier).
 */
export function buildHaven(): ZoneLayout {
  const rng = new Rng('haven');
  const b = new ZoneBuilder(
    {
      id: 'haven', name: 'Haven Isle', subtitle: 'Your stronghold on the Gleaming Sea', kind: 'haven', biome: 'meadow', levelRange: [16, 30], music: 'town',
      width: 170, depth: 170, seed: 3001, ground: ['grass', 'sand', 'cobble', 'dirt'], groundScale: [4, 5, 4, 5], hills: 0, waterLevel: WATER,
      ambient: 'pollen', mapTint: 0x2e5a4a,
    },
    rng,
  );
  const land = (x: number, z: number): number => landHeight(COAST, PROFILE, x, z);
  b.addHeight((x, z) => withPier(land(x, z), PIER, DECK, x, z));
  b.addSurface((x, z, w) => {
    const h = land(x, z);
    w[1] += (1 - smoothstep(WATER + 0.7, WATER + 1.8, h)) * 4;
  });

  // Roads and yards.
  b.road([{ x: 0, z: 39 }, { x: 0, z: 24 }, { x: 0, z: 6 }, { x: 0, z: -6 }], 3.4, 2, 0.5);
  b.road([{ x: 0, z: 6 }, { x: -12, z: 2 }, { x: -22, z: -4 }], 3, 2, 0.5);
  b.road([{ x: 0, z: 6 }, { x: 12, z: 0 }, { x: 20, z: -5 }], 3, 2, 0.5);
  b.road([{ x: 0, z: 21 }, { x: -9, z: 20 }, { x: -14, z: 17.5 }], 2.6, 2, 0.5);
  b.road([{ x: 0, z: 32 }, { x: 6, z: 31.5 }], 2.6, 2, 0.5);
  b.flat({ x: 0, z: -16 }, 12, { layer: 2 });
  b.flat({ x: -26, z: -7 }, 7, { layer: 3 });
  b.flat({ x: 23, z: -9 }, 6, { layer: 2 });
  b.flat({ x: -20, z: 17 }, 7.5, { layer: 3 });
  b.flat({ x: 8, z: 32 }, 4.5, { layer: 2 });

  // Arrival: pier, dock portal, a moored ship.
  layPier(b, PIER, DECK);
  const tip = pierTip(PIER, -1.8);
  b.portal({ id: 'haven_dock', to: 'sea', toSpawn: 'fromHaven', x: tip.x, z: tip.z, radius: 2.4, label: 'The Gleaming Sea', kind: 'dock' });
  b.spawn('fromSea', 0, 47, Math.PI);
  b.spawn('start', 0, 12, Math.PI);
  b.prop('ship_player', 5.3, 50, { rot: 0, y: floatY(b, 5.3, 50, WATER, -0.9) });
  for (const [x, z] of [[-2.6, 30], [2.6, 18], [-2.6, 6]] as const) b.prop('lamp_post', x, z, { collider: { r: 0.25 } });

  // Manor plaza.
  b.prop('house_a', 0, -31, { scale: 1.5, collider: { w: 6.3, d: 5.3 } });
  b.prop('house_b', -14, -26, { rot: 0.35, collider: { w: 7.4, d: 5.4 } });
  b.prop('house_c', 14.5, -27, { rot: -0.35, collider: { w: 5.0, d: 5.2 } });
  b.prop('statue_hero', 0, -12, { scale: 0.55, collider: { w: 2.6, d: 2.6 } });
  b.prop('bench', -5, -8, { collider: { w: 2, d: 0.6 } });
  b.prop('bench', 5, -8, { collider: { w: 2, d: 0.6 } });
  for (const [x, z] of [[-9, -9], [9, -9], [-9, -23], [9, -23]] as const) b.prop('lamp_post', x, z, { collider: { r: 0.25 } });
  b.prop('banner_pole', -6, -24, { collider: { r: 0.35 }, tint: 0x4a7ad0 });
  b.prop('banner_pole', 6, -24, { collider: { r: 0.35 }, tint: 0x4a7ad0 });
  b.npc('steward_pim', 4.5, -14.5, 0);
  b.reserve({ x: 0, z: -28 }, 14);

  // Workshop (west).
  b.interact({ id: 'haven_station_workshop', kind: 'station', x: -24.5, z: -6.5, data: { station: 'workshop' } });
  b.prop('forge_anvil', -27, -9, { rot: 0.3, collider: { w: 2.2, d: 1.2 } });
  b.prop('house_b', -32, -14, { scale: 0.7, rot: 0.5, collider: { w: 7.4, d: 5.4 } });
  b.prop('cart', -31, -3, { rot: 1.2, collider: { w: 1.8, d: 3.4 } });
  b.prop('crate', -29.5, -9.5, { collider: { w: 0.9, d: 0.9 } });
  b.prop('crate', -30.2, -8.7, { rot: 0.6, collider: { w: 0.9, d: 0.9 } });
  b.prop('barrel', -28.6, -10.8, { collider: { r: 0.45 } });

  // Research tower (east).
  b.interact({ id: 'haven_station_research', kind: 'station', x: 22.5, z: -8, data: { station: 'research' } });
  b.prop('tower', 25, -14, { collider: { r: 2.4 } });
  b.prop('notice_board', 19.5, -11.5, { rot: 0.4, collider: { w: 2.1, d: 0.5 } });
  b.prop('altar', 26.5, -6.5, { rot: -0.5, collider: { w: 2.2, d: 1.2 } });
  b.prop('rift_core', 28.5, -9, { scale: 0.35, collider: { r: 1.2 } });

  // Herb garden (south-west).
  b.interact({ id: 'haven_station_garden', kind: 'station', x: -16.5, z: 13, data: { station: 'garden' } });
  for (const [i, x] of [-25, -22, -19].entries()) {
    for (const [j, z] of [15, 18, 21].entries()) b.prop((i + j) % 2 === 0 ? 'bush_flower' : 'node_herb', x, z, { scale: 0.9, rot: x * z, noShadow: true });
  }
  b.prop('well', -27, 11.5, { collider: { r: 1.0 } });

  // Ship dispatch (by the pier).
  b.interact({ id: 'haven_station_dispatch', kind: 'station', x: 7.5, z: 30.5, data: { station: 'dispatch' } });
  b.prop('notice_board', 7.5, 33.3, { collider: { w: 2.1, d: 0.5 } });
  b.prop('crate', 10.2, 34, { collider: { w: 0.9, d: 0.9 } });
  b.prop('crate', 11, 33.3, { rot: 0.5, collider: { w: 0.9, d: 0.9 } });
  b.prop('barrel', 11.4, 35.2, { collider: { r: 0.45 } });

  // Scenery: orchards and bushes inland, palms on the beach, reeds at the waterline.
  const isle = { minX: -60, minZ: -60, maxX: 60, maxZ: 52 };
  const inland = (m: number) => (p: { x: number; z: number }) => shoreDistance(COAST, p.x, p.z) < -m;
  const beach = (p: { x: number; z: number }) => {
    const d = shoreDistance(COAST, p.x, p.z);
    return d < -1.5 && d > -6;
  };
  b.scatterProps([{ model: 'tree_oak', weight: 3, collider: { r: 0.45 }, scale: [0.9, 1.3] }, { model: 'tree_pine', weight: 1, collider: { r: 0.45 }, scale: [0.9, 1.2] }], 34, isle, 6, inland(6), 3);
  b.scatterProps([{ model: 'tree_palm', weight: 1, collider: { r: 0.4 }, scale: [0.9, 1.25] }], 16, isle, 7, beach, 2);
  b.scatterProps([{ model: 'bush', weight: 3, scale: [0.8, 1.2], noShadow: true }, { model: 'bush_flower', weight: 3, scale: [0.7, 1.1], noShadow: true }, { model: 'rock_b', weight: 1, collider: { r: 0.5 }, scale: [0.6, 1.1] }], 60, isle, 3.5, inland(3), 1);
  b.scatterProps([{ model: 'grass_tuft', weight: 5, scale: [0.8, 1.3], noShadow: true }], 160, isle, 2.2, inland(2), 0);
  b.scatterProps([{ model: 'reeds', weight: 1, noShadow: true }, { model: 'rock_a', weight: 0.4, scale: [0.6, 1.2] }], 40, { minX: -65, minZ: -65, maxX: 65, maxZ: 55 }, 4, (p) => {
    const d = shoreDistance(COAST, p.x, p.z);
    return d > -1.8 && d < 0.5;
  }, 1);

  return b.build();
}
